import React, { MouseEvent, useState } from 'react';

import CheckboxGroup from './components/checkbox-group/checkbox-group';
import { CheckSolidIcon } from '../../shared/icons/icons';
import Typography from '../typography/typography';

import cn from '../../shared/utils/cn/cn';

interface CheckboxProps {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
  id?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  label?: React.ReactNode;
  name?: string;
  onClick?: (value?: string, event?: MouseEvent) => void;
  value?: string;
}

function Checkbox(props: React.PropsWithChildren<CheckboxProps>) {
  const {
    ariaLabel,
    ariaLabelledBy,
    children,
    className = '',
    id,
    isChecked: isCheckedProp,
    isDisabled,
    label,
    name,
    onClick,
    value
  } = props;
  const [isChecked, setIsChecked] = useState(isCheckedProp || false);
  const isCheckedVal =
    typeof isCheckedProp === 'boolean' ? isCheckedProp : isChecked;
  const boxClassName =
    'relative flex h-4 w-4 items-center justify-center rounded-sm border-2 border-solid text-xs text-white peer-focus:outline peer-focus:outline-2 peer-focus:outline-offset-2 peer-focus:outline-blue-70';
  const boxStateClassNames = {
    'checked':
      'cursor-pointer border-blue-70 bg-blue-70 hover:border-blue-80 hover:bg-blue-80 active:border-blue-90 active:bg-blue-90',
    'checked-disabled': 'cursor-default border-gray-40 bg-gray-40',
    'unchecked':
      'cursor-pointer border-gray-100 bg-white hover:border-gray-50 active:border-gray-60',
    'unchecked-disabled': 'cursor-default border-gray-40 bg-white'
  };
  const boxState = `${isCheckedVal ? 'checked' : 'unchecked'}${
    isDisabled ? '-disabled' : ''
  }`;

  function handleClick(e) {
    e.preventDefault();
    if (!isDisabled) {
      if (onClick) {
        onClick(value, e);
      }

      if (typeof isCheckedProp !== 'boolean') {
        setIsChecked(!isChecked);
      }
    }
  }

  return (
    <Typography
      className={cn(
        'inline-flex select-none items-center focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-blue-70',
        `checkbox ${className} ${isCheckedVal ? 'checkbox--checked' : ''}`
      )}
      onClick={handleClick}
      onKeyPress={handleClick}
      role="none"
      tabIndex={label ? 0 : -1}
      tag="div"
      variant="P"
    >
      <span className="relative cursor-pointer pr-3 checkbox__container">
        <input
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          checked={isCheckedVal}
          className="peer absolute opacity-0"
          disabled={isDisabled}
          id={id}
          name={name}
          readOnly
          tabIndex={!label ? 0 : -1}
          type="checkbox"
          value={value}
        />

        <span
          className={cn(
            boxClassName,
            boxStateClassNames[boxState],
            'checkbox__icon'
          )}
        >
          <CheckSolidIcon className="relative z-[2] !h-[10px]" />
        </span>
      </span>
      {label && (
        <label
          className={
            isDisabled
              ? 'cursor-default text-gray-40'
              : 'cursor-pointer text-gray-100'
          }
          data-testid="checkbox_label"
          htmlFor={id}
        >
          {label}
        </label>
      )}
      {children}
    </Typography>
  );
}

export { CheckboxGroup };
export default Checkbox;
